import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { PortalTargetProvider } from '@/components/PortalTarget';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { FOCUSABLE, useModalDialog } from '@/hooks/useModalDialog';

export interface SideBarModalProps {
  isOpen: boolean;
  /** Asked to close: the parent sets `isOpen` false (and usually unmounts). */
  onClose: () => void;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  position?: 'center' | 'right';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  maxHeight?: 'screen' | 'auto';
  className?: string;
  contentRef?: React.Ref<HTMLDivElement>;
  /**
   * Unsaved changes inside. When true every close request (button, backdrop,
   * Escape, drag) asks for confirmation first. Defaults to
   * `position === 'right'` so the existing right-panel forms keep their guard
   * without caller changes (RESPONSIVE_WEB_PLAN, OV2).
   */
  isDirty?: boolean;
  /**
   * Phone presentation (below `md`). `auto`: `center` → bottom sheet,
   * `right` → full-height sheet.
   */
  sheet?: 'auto' | 'bottom' | 'full';
  /**
   * Takes the initial focus instead of the title. Only for a control that
   * does not pop the phone keyboard (a radio, a button), or the sheet's
   * reason to focus the title comes back.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

type Mode = 'center' | 'right' | 'bottom' | 'full';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const INTERACTIVE = 'button, a[href], input, select, textarea, [role="button"]';

/** Drag past this many px, or release faster than this, dismisses the sheet. */
const DRAG_DISMISS_PX = 120;
const DRAG_DISMISS_VELOCITY = 0.5; // px per ms
/** A flick shorter than this never counts, however fast (accidental taps). */
const DRAG_MIN_PX = 20;
/** Exit transition is 150ms (`duration-150`); the timeout fallback fires just after. */
const EXIT_FALLBACK_MS = 200;

export const MAX_WIDTH: Record<
  NonNullable<SideBarModalProps['width']>,
  string
> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[500px]',
  lg: 'max-w-[600px]',
  xl: 'max-w-[800px]',
  full: 'max-w-7xl',
};

// The <dialog> itself is never transformed or animated: a transformed
// ancestor becomes the containing block for `position: fixed`, which would
// misplace the menus that portal into it (RESPONSIVE_WEB_PLAN, OV1).
const DIALOG_LAYOUT: Record<Mode, string> = {
  center: 'items-center justify-center',
  right: 'items-stretch justify-end',
  bottom: 'items-end justify-center',
  full: 'items-stretch justify-center',
};

const WRAPPER_BASE =
  'relative bg-white flex flex-col min-h-0 transition-[transform,opacity] duration-150 ease-[cubic-bezier(0.2,0,0,1)]';

// `width` is itself a `max-w-*` utility, so never pair it with `max-w-full`:
// two max-width utilities on one element resolve by stylesheet order, and
// `max-w-full` wins, which stretched every desktop dialog to the full
// viewport. `w-full` plus the cap already shrinks below narrow screens.
/**
 * Exported for the unit test that guards the max-width composition; not part
 * of the component's public API.
 */
export const WRAPPER_LAYOUT = (mode: Mode, width: string): string => {
  switch (mode) {
    case 'center':
      return `w-full ${width} max-h-[90vh] rounded-lg shadow-2xl`;
    case 'right':
      return `w-full ${width} h-full shadow-2xl`;
    case 'bottom':
      return 'w-full rounded-t-[20px] max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.35)]';
    case 'full':
      return 'w-full h-full';
  }
};

const CloseIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

type DragState = { startY: number; startT: number };

/**
 * Modal on a native `<dialog>` (top layer, focus trap, `Escape`).
 *
 * Desktop/tablet keeps the centered dialog and right panel. Below `md` it is a
 * sheet: bottom sheet for `center`, full-height for `right` (or forced via
 * `sheet`). Dismissal goes through one path — close button, backdrop, Escape,
 * drag on the handle/header — which the dirty guard can intercept with an
 * exit-confirm rendered as a second `<dialog>` stacked in the top layer.
 *
 * The dialog node is the `PortalTargetProvider` value, so `ActionDropdown` and
 * `DateRangeFilter` inside mount their menus in the top layer with the sheet.
 */
const SideBarModal: React.FC<SideBarModalProps> = ({
  isOpen,
  onClose,
  header,
  children,
  footer,
  width = 'md',
  position = 'center',
  showCloseButton = true,
  closeOnBackdropClick = true,
  maxHeight = 'screen',
  className = '',
  contentRef,
  isDirty,
  sheet = 'auto',
  initialFocusRef,
}) => {
  const { isMobile } = useBreakpoint();

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeFiredRef = useRef(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const titleId = useId();

  const mode: Mode = !isMobile
    ? position
    : sheet === 'bottom' || (sheet === 'auto' && position === 'center')
      ? 'bottom'
      : 'full';
  const isSheet = mode === 'bottom' || mode === 'full';
  const guard = isDirty ?? position === 'right';

  // Fresh state on every open.
  useEffect(() => {
    setShowExitConfirm(false);
    setClosing(false);
    setIsDragging(false);
    closeFiredRef.current = false;
  }, [isOpen]);

  // The drag transform is written straight to the node (below), so React never
  // re-renders the sheet subtree at pointer-event rate. Clearing it here — in
  // the same commit that drops `transition-none` — keeps the spring-back
  // animated and hands the element back to the class-based animation.
  useLayoutEffect(() => {
    if (!isDragging && wrapperRef.current) {
      wrapperRef.current.style.transform = '';
    }
  }, [isDragging]);

  const finishClose = useCallback(() => {
    if (closeFiredRef.current) return;
    closeFiredRef.current = true;
    onCloseRef.current();
  }, []);

  /** Past the guard: animate out, then tell the parent. */
  const beginClose = useCallback(() => {
    if (prefersReducedMotion()) {
      finishClose();
      return;
    }
    setClosing(true);
  }, [finishClose]);

  const requestClose = useCallback(() => {
    if (closing) return;
    if (guard) {
      setShowExitConfirm(true);
      return;
    }
    beginClose();
  }, [closing, guard, beginClose]);

  // Exit animation: `transitionend` on the wrapper ends it, the timeout is the
  // safety net (tab in background, transition never fired).
  useEffect(() => {
    if (!closing) return undefined;
    const timer = window.setTimeout(finishClose, EXIT_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [closing, finishClose]);

  const handleTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>
  ) => {
    if (closing && event.target === event.currentTarget) finishClose();
  };

  // Focus lands on the sheet title, not the first field: on a phone focusing an
  // input pops the keyboard the moment the sheet opens (RESPONSIVE_WEB_PLAN,
  // accessibility checklist). Falls back to the first focusable when there is
  // no header to focus.
  const getInitialFocus = useCallback(
    (dialog: HTMLDialogElement) =>
      initialFocusRef?.current ??
      titleRef.current ??
      bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      dialog.querySelector<HTMLElement>(FOCUSABLE),
    [initialFocusRef]
  );

  // showModal()/open fallback, Escape, backdrop, scroll lock, focus in/out.
  const { dialogEl, setDialogRef, isNativeModal, dialogProps } = useModalDialog(
    {
      isOpen,
      onRequestClose: requestClose,
      // The browser closed it on its own (Android back gesture via CloseWatcher
      // without activation, form method="dialog"): nothing left to animate.
      onNativeClose: finishClose,
      closeOnBackdropClick,
      getInitialFocus,
    }
  );

  const setBodyRef = useCallback(
    (node: HTMLDivElement | null) => {
      bodyRef.current = node;
      if (typeof contentRef === 'function') {
        contentRef(node);
      } else if (contentRef && 'current' in contentRef) {
        (contentRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;
      }
    },
    [contentRef]
  );

  // Drag-to-dismiss, bound to the handle/header only so the scrolling body is
  // never mistaken for a drag (RESPONSIVE_WEB_PLAN, Template 3).
  const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (!isSheet || closing) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // A press on the close button (or any control in the header) is a click,
    // not a drag: capturing the pointer here would retarget its `click`.
    if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
    dragRef.current = { startY: event.clientY, startT: event.timeStamp };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  // Written straight to the node: a `setState` per `pointermove` would
  // re-render the whole sheet subtree (often a large form) at pointer rate.
  const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dy = Math.max(0, event.clientY - drag.startY);
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translateY(${dy}px)`;
    }
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    // The transform is cleared by the layout effect on this same commit.
    setIsDragging(false);
    const dy = Math.max(0, event.clientY - drag.startY);
    const velocity = dy / Math.max(1, event.timeStamp - drag.startT);
    if (
      dy > DRAG_DISMISS_PX ||
      (dy > DRAG_MIN_PX && velocity > DRAG_DISMISS_VELOCITY)
    ) {
      requestClose();
    }
  };

  const dragHandlers = isSheet
    ? {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerEnd,
        onPointerCancel: handlePointerEnd,
      }
    : {};

  if (!isOpen) return null;

  const hasHeaderRow = Boolean(header || showCloseButton);
  const headerIsText = typeof header === 'string' || typeof header === 'number';
  const labelProps = header
    ? { 'aria-labelledby': titleId }
    : { 'aria-label': 'Dialog' };

  const wrapperClasses = [
    WRAPPER_BASE,
    WRAPPER_LAYOUT(mode, MAX_WIDTH[width]),
    closing
      ? isSheet
        ? 'translate-y-full'
        : 'opacity-0'
      : isSheet
        ? 'animate-slide-up'
        : 'animate-fade-in',
    isDragging ? 'transition-none' : '',
    isSheet && !footer ? 'pb-[env(safe-area-inset-bottom)]' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <dialog
        ref={setDialogRef}
        // Implicit role is dialog; aria-modal is explicit because the `open`
        // fallback path is not modal on its own.
        aria-modal="true"
        {...labelProps}
        {...dialogProps}
        data-mode={mode}
        className={`p-0 m-0 border-0 max-w-none max-h-none w-screen h-screen supports-[height:100dvh]:h-[100dvh] overflow-visible open:flex ${
          DIALOG_LAYOUT[mode]
        } ${
          isNativeModal
            ? 'bg-transparent backdrop:bg-black/50'
            : 'fixed inset-0 z-50 bg-black/50'
        }`}
      >
        <PortalTargetProvider value={dialogEl}>
          {/* Layout and motion live on the wrapper, never on the <dialog>. */}
          <div
            ref={wrapperRef}
            data-testid="sidebar-modal-wrapper"
            className={wrapperClasses}
            onTransitionEnd={handleTransitionEnd}
          >
            {mode === 'bottom' && (
              <div
                data-testid="sheet-handle"
                aria-hidden="true"
                className="shrink-0 pt-2.5 pb-1 cursor-grab touch-none"
                {...dragHandlers}
              >
                <div className="h-1 w-10 rounded bg-gray-200 mx-auto" />
              </div>
            )}

            {hasHeaderRow && (
              <div
                className={`flex items-center justify-between border-b border-gray-200 shrink-0 ${
                  isSheet ? 'px-4 py-3 touch-none select-none' : 'px-6 py-4'
                }`}
                {...dragHandlers}
              >
                <div className="flex-1 min-w-0">
                  {/* tabIndex -1 so the title can take the initial focus. */}
                  {headerIsText ? (
                    <h2
                      ref={titleRef as React.Ref<HTMLHeadingElement>}
                      id={titleId}
                      tabIndex={-1}
                      className="text-lg font-semibold text-gray-900 outline-none"
                    >
                      {header}
                    </h2>
                  ) : header ? (
                    <div
                      ref={titleRef as React.Ref<HTMLDivElement>}
                      id={titleId}
                      tabIndex={-1}
                      className="outline-none"
                    >
                      {header}
                    </div>
                  ) : null}
                </div>
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={requestClose}
                    className={`absolute right-2 z-10 flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors md:h-9 md:w-9 md:right-4 ${
                      mode === 'bottom' ? 'top-4' : 'top-2 md:top-3'
                    }`}
                    aria-label="Tutup"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div
              ref={setBodyRef}
              className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${
                maxHeight === 'screen' ? 'max-h-full' : ''
              }`}
            >
              {children}
            </div>

            {footer && (
              <div
                data-tap-region
                className={`border-t border-gray-200 shrink-0 ${
                  isSheet
                    ? 'px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
                    : 'p-6'
                }`}
              >
                {footer}
              </div>
            )}
          </div>
        </PortalTargetProvider>
      </dialog>

      <ConfirmDialog
        isOpen={showExitConfirm}
        title="Batalkan perubahan?"
        description="Perubahan yang belum disimpan akan hilang."
        cancelLabel="Lanjut mengisi"
        confirmLabel="Buang"
        confirmVariant="danger"
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={() => {
          setShowExitConfirm(false);
          beginClose();
        }}
      />
    </>
  );
};

export default SideBarModal;
